import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";

function NewCoupon({ onDone }) {
  const [f, setF] = useState({ code: "", discount_type: "percentage", value: 10, min_cart: "", usage_limit: "", active: true });
  const [open, setOpen] = useState(false);
  const save = async () => {
    if (!f.code) return toast.error("Code required");
    try { await api.post("/admin/coupons", { ...f, value: Number(f.value), min_cart: f.min_cart ? Number(f.min_cart) : null, usage_limit: f.usage_limit ? Number(f.usage_limit) : null }); toast.success("Coupon created"); setOpen(false); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-teal hover:bg-teal-dark text-white gap-1" data-testid="new-coupon-btn"><Plus className="w-4 h-4" /> New Coupon</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Coupon</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="coupon-code" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Type</Label><select className="w-full border rounded-lg px-3 py-2 text-sm" value={f.discount_type} onChange={(e) => setF({ ...f, discount_type: e.target.value })}><option value="percentage">Percentage</option><option value="fixed">Fixed ₹</option></select></div>
            <div><Label className="text-xs">Value</Label><Input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="coupon-value" /></div>
            <div><Label className="text-xs">Min cart ₹</Label><Input type="number" value={f.min_cart} onChange={(e) => setF({ ...f, min_cart: e.target.value })} /></div>
            <div><Label className="text-xs">Usage limit</Label><Input type="number" value={f.usage_limit} onChange={(e) => setF({ ...f, usage_limit: e.target.value })} /></div>
          </div>
          <Button className="w-full bg-teal hover:bg-teal-dark text-white" onClick={save} data-testid="coupon-save">Create</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminCoupons() {
  const qc = useQueryClient();
  const { data: coupons, isLoading } = useQuery({ queryKey: ["admin-coupons"], queryFn: async () => (await api.get("/admin/coupons")).data });
  const toggle = async (c) => { await api.put(`/admin/coupons/${c.id}`, { active: !c.active }); qc.invalidateQueries(["admin-coupons"]); };
  const del = async (id) => { if (!window.confirm("Delete coupon?")) return; await api.delete(`/admin/coupons/${id}`); qc.invalidateQueries(["admin-coupons"]); };
  if (isLoading) return <Loader2 className="w-8 h-8 animate-spin text-teal" />;
  return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="text-2xl font-bold text-white">Coupons</h1><NewCoupon onDone={() => qc.invalidateQueries(["admin-coupons"])} /></div>
      <div className="bg-slate-900 border border-white/10 rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-800/50 text-slate-400 text-xs uppercase"><tr><th className="text-left px-4 py-3">Code</th><th className="text-left px-4 py-3">Discount</th><th className="text-left px-4 py-3">Used</th><th className="text-left px-4 py-3">Active</th><th className="text-right px-4 py-3"></th></tr></thead>
          <tbody className="divide-y divide-white/5">
            {coupons.map((c) => (
              <tr key={c.id} className="hover:bg-white/5" data-testid={`coupon-${c.code}`}>
                <td className="px-4 py-3 font-mono text-white">{c.code}</td>
                <td className="px-4 py-3 text-slate-300">{c.discount_type === "percentage" ? `${c.value}%` : `₹${c.value}`}{c.min_cart ? ` (min ₹${c.min_cart})` : ""}</td>
                <td className="px-4 py-3 text-slate-400">{c.used_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                <td className="px-4 py-3"><Switch checked={c.active} onCheckedChange={() => toggle(c)} /></td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(c.id)} className="p-2 text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
